<template>
  <div>
    <div class="text-xl font-semibold">
      <router-link v-slot="{ href, route, navigate }" to="/companies" custom>
        <li class="list-none">
          <a :href="href" @click="navigate">{{ route.meta.name }}</a>
          <span v-if="company"> / {{ company.name }}</span>
        </li>
      </router-link>
    </div>
    <LoadingSpinner v-if="$apollo.queries.company.loading" />
    <div
      v-else-if="company"
      class="container grid grid-cols-1 mt-4 lg:grid-cols-2 gap-2"
    >
      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-2 gap-2">
          <BaseInput
            v-model="company.companyNumber"
            disabled
            label="Företagsnummer"
          />
          <div>
            <span
              class="block mb-2 text-sm font-semibold tracking-wide text-gray-900"
            >
              Företagstyp
            </span>
            <select
              id=""
              v-model="company.companyType"
              name=""
              class="form-control"
            >
              <option value="SALOON">Salong</option>
              <option value="SHELL">Skalbolag</option>
              <option value="RENTAL_COMPANY">Hyrstol</option>
              <option value="SUPPLIER">Leverantör</option>
            </select>
          </div>
          <BaseInput
            v-model="company.name"
            label="Företagsnamn"
            internal-lock
          />
          <BaseInput
            v-model="company.displayname"
            label="Visningsnamn"
            internal-lock
          />
          <BaseInput
            v-model="company.VAT"
            label="Organisationsnummer"
            internal-lock
          />

          <BaseInput
            v-model="company.urlname"
            internal-lock
            :label="`bokning.voady.se/${company.urlname || ''}`"
          />
          <BaseInput
            v-model="company.voadyFortnoxId"
            label="Voady FortnoxID"
            internal-lock
          />
          <BaseInput
            v-model="company.invoiceEmail"
            label="Faktura e-post"
            internal-lock
          />

          <div
            v-if="company.companyType == 'RENTAL_COMPANY'"
            class="p-2 mt-4 bg-gray-100 shadow-xs"
          >
            <span
              class="block mb-1 text-sm font-semibold tracking-wide text-gray-900"
            >
              Hyrstol till
            </span>
            <select id="" v-model="parentCompany" name="" class="form-control">
              <option :value="null">Ingen hyrstol</option>
              <option
                v-for="company of companies"
                :key="company.id"
                :value="company.id"
                >{{ company.name }}</option
              >
            </select>
          </div>
        </div>
        <div class="flex flex-row flex-wrap gap-x-2">
          <div class="flex flex-col py-2">
            <v-date-picker
              v-model="company.activationDate"
              show-iso-weeknumbers
              color="teal"
              locale="sv-SE"
              :popover="{ visibility: 'click' }"
            >
              <template #default="{ inputValue, inputEvents }">
                <label for="activationDate">Aktiveringsdatum?</label>
                <div
                  class="p-2 text-gray-900 transition-colors duration-200 bg-white border rounded-sm"
                >
                  <input
                    :value="inputValue"
                    class="focus:outline-none"
                    v-on="inputEvents"
                  />
                </div>
              </template>
            </v-date-picker>
          </div>
          <div class="flex flex-col py-2">
            <v-date-picker
              v-model="company.disabledDate"
              show-iso-weeknumbers
              color="teal"
              locale="sv-SE"
              :popover="{ visibility: 'click' }"
            >
              <template #default="{ inputValue, inputEvents }">
                <label for="activationDate">Inaktiveringsdatum?</label>
                <div
                  class="p-2 text-gray-900 transition-colors duration-200 bg-white border rounded-sm"
                >
                  <input
                    :value="inputValue"
                    class="focus:outline-none"
                    v-on="inputEvents"
                  />
                </div> </template
            ></v-date-picker>
          </div>
          <div class="flex flex-row items-center py-2 space-x-2">
            <input id="isChurn" v-model="company.isChurn" type="checkbox" />
            <label for="isChurn">Churn?</label>
          </div>
        </div>
        <div class="flex flex-row">
          <div class="flex-1">
            <BaseLabel label="Salonger" />
            <div
              v-if="company && company.saloons && company.saloons.length"
              class="flex flex-row flex-wrap space-x-2"
            >
              <div v-for="s of company.saloons" :key="s.id">
                <span
                  class="p-4 px-2 py-1 text-xs font-semibold rounded-full bg-lagon-100 text-lagon-700"
                >
                  {{ s.name }}
                </span>
              </div>
            </div>
            <h3 v-else class="mt-4 text-center text-gray-700">Inga salonger</h3>
          </div>
        </div>
        <div class="grid grid-cols-2  gap-x-2">
          <div class="flex flex-row pt-2 border-t">
            <div class="flex-1">
              <div class="flex justify-between">
                <BaseLabel label="Integrerad Terminal" />
                <button
                  class="btn btn-outline-secondary whitespace-nowrap mr-2"
                  @click="createTerminal"
                >
                  Lägg till
                </button>
              </div>
              <div v-if="company.terminals.length">
                <div
                  v-for="t of company.terminals"
                  :key="t.id"
                  class="px-4 py-2 mt-1  transition-colors duration-75 border-l-2 shadow-xs border-lagon-300 hover:bg-gray-100 hover:cursor-pointer"
                  @click="terminalDetails(t.id)"
                >
                  <p class="font-semibold leading-none pb-1">
                    {{ t.saloon.name }}
                  </p>
                  <div class="flex items-center flex-wrap gap-x-2">
                    <strong
                      :class="
                        t.provider === 'ADYEN'
                          ? 'bg-lagon-700 text-lagon-50'
                          : 'bg-amber-700 text-amber-50'
                      "
                      class="px-1 py-px text-xs rounded inline-block"
                    >
                      {{ t.provider }}
                    </strong>
                    <span>{{ t.terminalId }}</span>
                  </div>

                  <span>{{ t.displayName }}</span>
                </div>
              </div>
              <h3 v-else class="mt-2 text-center text-gray-700">
                Inga terminaler
              </h3>
            </div>
          </div>

          <div
            v-if="
              company.companyType !== 'SUPPLIER' ||
                company.companyType !== 'RENTAL_COMPANY'
            "
            class="flex flex-row pt-2 border-t"
          >
            <div class="flex-1">
              <div class="flex justify-between">
                <BaseLabel label="Hyrstolar" />
                <button
                  class="btn btn-outline-secondary whitespace-nowrap mr-2"
                  @click="createRentalchair"
                >
                  Lägg till
                </button>
              </div>
              <div
                v-if="
                  company && company.rentalChairs && company.rentalChairs.length
                "
              >
                <div
                  v-for="c of company.rentalChairs"
                  :key="c.id"
                  class="p-4 mt-1 font-semibold transition-colors duration-75 border-l-2 shadow-xs border-lagon-300 hover:bg-gray-100 hover:cursor-pointer"
                  @click="
                    $router.push({
                      name: 'company.subdetails',
                      params: { id: company.id, subId: c.id },
                    })
                  "
                >
                  {{ c.name }}
                </div>
              </div>
              <h3 v-else class="mt-2 text-center text-gray-700">
                Inga hyrstolar
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div>
        <BaseLabel label="Moduler" />
        <table class="table w-full">
          <thead>
            <th />
            <th />
            <th>A pris</th>
            <th>Antal</th>
            <th>Total</th>
          </thead>
          <tbody>
            <tr>
              <td>
                <input v-model="company.moduleVoadyBase" type="checkbox" />
              </td>
              <td>Voady BAS</td>
              <td>99:-</td>
              <td />
              <td />
            </tr>
            <tr>
              <td>
                <input
                  v-model="company.moduleOnlineBookingPremium"
                  type="checkbox"
                />
              </td>
              <td>Onlinebokning Premium</td>
              <td>349:-</td>
              <td />
              <td />
            </tr>
            <tr>
              <td />
              <td>Kassa</td>
              <td>99:-</td>
              <td>
                <span v-if="company.terminals.length">
                  {{ company.cashregisters.length }}
                </span>
              </td>

              <td>
                <span v-if="company.terminals.length">{{
                  company.cashregisters.length * 99
                }}</span>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  v-model="company.settingsIntegratedTerminalEnabled"
                  type="checkbox"
                />
              </td>
              <td>Integrerad Terminal</td>
              <td>99:-</td>
              <td>
                <span v-if="company.terminals.length">{{
                  company.terminals.length
                }}</span>
              </td>
              <td>
                <span v-if="company.terminals.length">
                  {{ company.terminals.length * 99 }}:-
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <input v-model="company.moduleAccounting" type="checkbox" />
              </td>
              <td>Bokföring</td>
              <td>199:-</td>
              <td>
                <span v-if="company.moduleAccounting">1</span>
              </td>
              <td>
                <span v-if="company.moduleAccounting">199:-</span>
              </td>
            </tr>
            <tr v-if="company.moduleAccounting">
              <td colspan="5">
                <div>
                  <v-date-picker
                    v-model="company.moduleAccountingStart"
                    show-iso-weeknumbers
                    color="teal"
                    locale="sv-SE"
                    :popover="{ visibility: 'click' }"
                  >
                    <template #default="{ inputValue, inputEvents }">
                      <label for="activationDate">Aktiveringsdatum?</label>
                      <div
                        class="p-2 text-gray-900 transition-colors duration-200 bg-white border rounded-sm"
                      >
                        <input
                          :value="inputValue"
                          class="focus:outline-none"
                          v-on="inputEvents"
                        />
                      </div> </template
                  ></v-date-picker>
                </div>
              </td>
            </tr>
            <tr>
              <td><input v-model="company.moduleFortnox" type="checkbox" /></td>
              <td>Fortnox</td>
              <td>199:-</td>
              <td>
                <span v-if="company.moduleFortnox">1</span>
              </td>
              <td>
                <span v-if="company.moduleFortnox">199:-</span>
              </td>
            </tr>
            <!-- <tr v-if="company.moduleFortnox">
              <td colspan="5">
                <div class="px-3 py-2 pt-3 bg-gray-100">
                  <h1 class="font-semibold">
                    Fortnox
                    <span
                      v-if="company.moduleFortnoxApiKey"
                      class="p-2 ml-2 text-sm font-bold text-green-700 bg-green-100 rounded-full"
                    >
                      Aktiverad
                    </span>
                    <span
                      v-else
                      class="p-2 ml-2 text-sm font-bold text-red-700 bg-red-100 rounded-full"
                    >
                      Ej aktiverad
                    </span>
                  </h1>
                  <div class="mt-2"></div>
                  <div class="flex flex-row item-center">
                    <BaseInput
                      v-model="fortnoxAuthCode"
                      label="Fortnoxkod"
                      class="mt-3"
                    />
                    <div class="mt-10 ml-3">
                      <button
                        @click="sendFortnoxKey"
                        class="btn btn-outline-secondary"
                        :disabled="fortnoxAuthCode.length < 5"
                      >
                        Spara
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr> -->
            <tr>
              <td>
                <input v-model="company.moduleResources" type="checkbox" />
              </td>
              <td>Resursbokning</td>
              <td>99:-</td>
              <td><span v-if="company.moduleResources">1</span></td>
              <td><span v-if="company.moduleResources">99:-</span></td>
            </tr>
            <tr>
              <td>
                <input
                  v-model="company.moduleMultiSpotBookings"
                  type="checkbox"
                />
              </td>
              <td>Flerkundsbokning</td>
              <td>99:-</td>
              <td><span v-if="company.moduleMultiSpotBookings">1</span></td>
              <td><span v-if="company.moduleMultiSpotBookings">99:-</span></td>
            </tr>
            <tr>
              <td>
                <input v-model="company.moduleSalaries" type="checkbox" />
              </td>
              <td colspan="4">Lön</td>
            </tr>
            <tr v-if="company.moduleSalaries && company.moduleFortnox">
              <td>
                <input
                  v-model="company.moduleSalariesFortnox"
                  type="checkbox"
                />
              </td>
              <td colspan="4">Fortnox Lön</td>
            </tr>
            <tr>
              <td>
                <input v-model="company.moduleReco" type="checkbox" />
              </td>
              <td colspan="4">Reco</td>
            </tr>
            <tr>
              <td>
                <input
                  v-model="company.moduleRentalchairInvoicing"
                  type="checkbox"
                />
              </td>
              <td>Hyrstolsfakturering</td>
              <td colspan="4">219:-</td>
            </tr>

            <tr v-if="smsReport.length">
              <td />
              <td>SMS</td>
              <td>0.85:-</td>
              <td>{{ smsReport[0].units }}</td>
              <td>{{ smsReport[0].units * 0.85 }}:-</td>
            </tr>
          </tbody>
        </table>
        <div>
          <BaseLabel label="Funktioner" />
          <div class="border-gray-300 divide-y select-none ">
            <div class="flex flex-row items-center py-2 space-x-2">
              <input
                id="settingsFlexCashregisters"
                v-model="company.settingsFlexCashregisters"
                type="checkbox"
              />
              <label for="settingsFlexCashregisters"
                >Flexibelt kassaregister</label
              >
            </div>
            <div class="flex flex-row items-center py-2 space-x-2 ">
              <input
                id="settingsCashierFlexProductSales"
                v-model="company.settingsCashierFlexProductSales"
                type="checkbox"
              />
              <label for="settingsCashierFlexProductSales"
                >Samlat produktköp i lager</label
              >
            </div>
            <div class="flex flex-row items-center py-2 space-x-2">
              <input
                id="moduleGiveBack"
                v-model="company.moduleGiveBack"
                type="checkbox"
              />
              <label for="moduleGiveBack">Give back</label>
            </div>
            <div class="flex flex-row items-center py-2 space-x-2">
              <input
                id="moduleMarketing"
                v-model="company.moduleMarketing"
                type="checkbox"
              />
              <label for="moduleMarketing">Marketing</label>
            </div>
          </div>
        </div>
      </div>
      <div
        class="flex justify-between grid-cols-2 pt-4  border-t border-gray-300"
      >
        <div class="flex flex-row gap-x-2">
          <div class="flex flex-row items-center py-2 space-x-2 font-semibold">
            <input id="enabled" v-model="company.enabled" type="checkbox" />
            <label for="enabled">Aktiverad</label>
          </div>
          <button
            class="btn btn-outline-secondary"
            :disabled="emailSent"
            @click="sendWelcomeEmail"
          >
            Skicka inloggningsuppgifter
          </button>
            <div class="flex flex-row items-center py-2 space-x-2 font-semibold ml-4">
            <input id="stageEnabled" v-model="company.hasAccessToStageEnvironment" type="checkbox" />
            <label for="stageEnabled">Stage tillträde</label>
          </div>
        </div>
        <div class="flex flex-row gap-x-2">
          <div class="flex flex-row items-center py-2 space-x-2 font-semibold">
            <input
              id="is-payment-overdue"
              v-model="company.isPaymentOverdue"
              type="checkbox"
            />
            <label for="is-payment-overdue">Obetald faktura</label>
          </div>
        </div>
        <div>
          <button class="btn btn-secondary" @click="handleSubmit">
            Spara
          </button>
        </div>
      </div>
      <transition name="fade">
        <div v-if="isOpen" class="modal-container" @click.self="close">
          <router-view @refresh="$apollo.queries.company.refetch()" />
        </div>
      </transition>
    </div>
    <CreateCompany @refresh="$apollo.queries.company.refetch()" />
  </div>
</template>

<script>
import UpdateCompany from '@/graphql/UpdateCompany.gql'
import SendWelcomeEmail from '@/graphql/SendWelcomeEmail.gql'
import CreateFortnoxConnection from '@/graphql/CreateFortnoxConnection.gql'
import GET_COMPANY from '@/graphql/Company.gql'
import { useSmsReport } from '@/composables/use-smsreport'
import CreateCompany from './CreateCompany.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useCompanies } from '@/composables/use-companies'
export default {
  components: { CreateCompany, LoadingSpinner },
  setup(props, { root }) {
    const { companies } = useCompanies()
    const { smsReport, loading, range } = useSmsReport(root.$route.params.id)
    return { companies, range, smsReport }
  },

  apollo: {
    company: {
      query: GET_COMPANY,
      variables() {
        return {
          id: this.$route.params.id,
        }
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  data() {
    return {
      company: null,
      emailSent: false,
      fortnoxAuthCode: '',
    }
  },
  computed: {
    isOpen() {
      return (
        this.$route.name === 'company.terminal' ||
        this.$route.name === 'company.subcreate' ||
        this.$route.name === 'company.subdetails' ||
        this.$route.name === 'company.terminalcreate'
      )
    },
    parentCompany: {
      get() {
        if (this.company.parentCompany) {
          return this.company.parentCompany.id
        }
        return null
      },
      set(val) {
        this.company.parentCompany = { id: val }
      },
    },
  },
  methods: {
    createTerminal() {
      this.$router.push({
        name: 'company.terminalcreate',
        params: { id: this.$route.params.id },
      })
    },
    createRentalchair() {
      this.$modal.show('create-company', {
        parentCompanyId: this.$route.params.id,
      })
    },
    close() {
      this.$router.push({
        name: 'companydetails',
        params: { id: this.$route.params.id },
      })
    },
    terminalDetails(id) {
      this.$router.push({
        name: 'company.terminal',
        params: { id: this.$route.params.id, terminalId: id },
      })
    },
    async handleSubmit() {
      await this.$apollo.mutate({
        mutation: UpdateCompany,
        variables: {
          data: {
            name: this.company.name,
            VAT: this.company.VAT,
            displayname: this.company.displayname,
            urlname: this.company.urlname,
            settingsCashierFlexProductSales: this.company
              .settingsCashierFlexProductSales,
            moduleGiveBack: this.company.moduleGiveBack,
            companyType: this.company.companyType,
            moduleMarketing: this.company.moduleMarketing,
            moduleSalaries: this.company.moduleSalaries,
            moduleSalariesFortnox: this.company.moduleSalariesFortnox,
            moduleReco: this.company.moduleReco,
            moduleResources: this.company.moduleResources,
            settingsFlexCashregisters: this.company.settingsFlexCashregisters,
            moduleFortnox: this.company.moduleFortnox,
            voadyFortnoxId: this.company.voadyFortnoxId,
            invoiceEmail: this.company.invoiceEmail,
            isChurn: this.company.isChurn,
            moduleVoadyBase: this.company.moduleVoadyBase,
            moduleOnlineBookingPremium: this.company.moduleOnlineBookingPremium,
            disabledDate: this.company.disabledDate,
            activationDate: this.company.activationDate,
            enabled: this.company.enabled,
            isPaymentOverdue: this.company.isPaymentOverdue,
            moduleRentalchairInvoicing: this.company.moduleRentalchairInvoicing,
            hasAccessToStageEnvironment: this.company.hasAccessToStageEnvironment,

            moduleAccounting: this.company.moduleAccounting,
            moduleAccountingStart:
              this.company.moduleAccounting &&
              this.company.moduleAccountingStart,
            moduleMultiSpotBookings: this.company.moduleMultiSpotBookings,
            settingsIntegratedTerminalEnabled: this.company
              .settingsIntegratedTerminalEnabled,

            parentCompany: this.company.parentCompany
              ? this.company.parentCompany.id
              : null,
          },
          id: this.$route.params.id,
        },
      })
      this.$notify({
        title: 'OK',
        text: 'Information sparad',
      })
    },
    async sendWelcomeEmail() {
      const confirm = window.confirm(
        'Skicka inloggningsuppgifter till förutbestämd e-post?'
      )
      if (!confirm) {
        const response = window.prompt('Skriv in E-post')
        if (response && response.length > 0) {
          await this.$apollo.mutate({
            mutation: SendWelcomeEmail,
            variables: {
              id: this.$route.params.id,
              email: response,
            },
          })
          this.emailSent = true
        }
      } else {
        await this.$apollo.mutate({
          mutation: SendWelcomeEmail,
          variables: {
            id: this.$route.params.id,
          },
        })
        this.emailSent = true
      }
    },
    async sendFortnoxKey() {
      const response = window.confirm(
        'Är du säker, nyckeln kommer att skriva över befintlig nyckel'
      )
      if (response === true) {
        await this.$apollo.mutate({
          mutation: CreateFortnoxConnection,
          variables: {
            data: {
              companyId: this.$route.params.id,
              authCode: this.fortnoxAuthCode,
            },
          },
        })

        this.fortnoxAuthCode = ''
        this.$apollo.queries.company.refetch()
      }
    },
  },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
